import { StorageType } from "@/types/admin-region";

export interface StorageDriveStatsDto {
  endpoint: string;
  state: string;
  totalBytes: number;
  usedBytes: number;
  availableBytes: number;
}

export interface StorageServerStatsDto {
  endpoint: string;
  state: string;
  uptimeSeconds?: number;
  version?: string;
  drives: StorageDriveStatsDto[];
}

export interface RegionStorageStatsDto {
  region: string;
  storageType: StorageType;
  endpoint: string | null;
  bucket: string;
  reachable: boolean;
  fetchedAt: string;
  totalBytes: number | null;
  usedBytes: number | null;
  freeBytes: number | null;
  usagePercent: number | null;
  objectBytes: number | null;
  objectCount: number | null;
  bucketCount: number | null;
  servers: StorageServerStatsDto[];
  error: string | null;
}

export interface RegionStorageErrorDto {
  region: string;
  message: string;
}

export interface AggregateStorageStatsDto {
  totalBytes: number;
  usedBytes: number;
  freeBytes: number;
  usagePercent: number;
  regions: RegionStorageStatsDto[];
  errors: RegionStorageErrorDto[];
}

export type TenantStorageSortBy =
  | "totalBytes"
  | "fileCount"
  | "tenantName"
  | "region";

export type SortOrder = "asc" | "desc";

export interface TenantStorageFilters {
  region?: string;
  search?: string;
  status?: string;
  sortBy?: TenantStorageSortBy;
  sortOrder?: SortOrder;
  limit?: number;
  offset?: number;
}

export interface TenantStorageUsageDto {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  region: string;
  status: string;
  fileCount: number;
  totalBytes: number;
}

export interface TenantStorageUsageListDto {
  data: TenantStorageUsageDto[];
  total: number;
  limit: number;
  offset: number;
  totalBytes: number;
  totalFiles: number;
}

export type TenantFilesSortBy = "filesize" | "filename" | "createdAt";

export interface TenantFilesFilters {
  search?: string;
  uploaded?: boolean;
  sortBy?: TenantFilesSortBy;
  sortOrder?: SortOrder;
  limit?: number;
  offset?: number;
}

export interface TenantFileDto {
  id: string;
  filename: string;
  filesize: number;
  uploaded: boolean;
  createdAt: string;
}

export interface TenantStorageFilesDto {
  tenantId: string;
  tenantName: string;
  region: string;
  data: TenantFileDto[];
  total: number;
  limit: number;
  offset: number;
  totalBytes: number;
}

export const TENANT_STATUS_ACTIVE = "Active";
export const TENANT_STATUS_SUSPENDED = "Suspended";

export type CanonicalTenantStatus =
  | typeof TENANT_STATUS_ACTIVE
  | typeof TENANT_STATUS_SUSPENDED;

export const normalizeTenantStatus = (status: string): string => {
  if (!status) return status;
  const lower = status.toLowerCase();
  if (lower === "active" || lower === "activated") {
    return TENANT_STATUS_ACTIVE;
  }
  if (lower === "suspended") {
    return TENANT_STATUS_SUSPENDED;
  }
  return status;
};
